import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { navigate } from 'vike/client/router';
import { useQueryClient } from '@tanstack/react-query';
import type { ApplicationState, Job } from '../../../types/tracked-job';
import type { UserStateMap } from '../../../types/user-state';
import { extractJobsFromUserState } from '../../../utils/user-state/extract-jobs-from-user-state';
import { userStateQueryKey } from '../../../query/user-state/userStateQueryKeys';
import { useSavedJobActions } from '../../../hooks/useSavedJobActions';
import { useUserJobStateSync } from '../../../hooks/useUserJobStateSync';
import { useAuth } from '../../../hooks/useAuth';
import { trackJobSave, trackJobUnsave, trackJobApply, trackEvent } from '../../../utils/analytics/analytics';
import { APPLICATION_STATES } from '../../application-state';
import type { DisplayJob } from '../../../types';
import type { ToastAction } from '../../../contexts/ToastContext';
import { normalizeSupportedLocale } from '../../../utils/routing/locale-from-pathname';
import { localizeRoute } from '../../../utils/constants/url-constants';
import { useFormatMessage } from '../../../i18n-review/client/Msg';

export interface UseHitActionsProps {
  hit: DisplayJob;
  jobId: string;
  isSaved: boolean;
  isViewed: boolean;
  isApplied: boolean;
  isHidden: boolean;
  savedJobPayload: DisplayJob & { id: string };
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number, action?: ToastAction) => void;
  onMenuClose?: () => void;
}

export interface HitActions {
  handleToggleSaved: () => Promise<void>;
  handleMarkViewed: () => Promise<void>;
  handleToggleApplied: () => Promise<void>;
  handleToggleHide: () => void;
  handleSetApplicationState: (state: ApplicationState) => Promise<void>;
  /** Exposed so callers don't need their own separate `useUserJobStateSync()` instance just for this. */
  recordBrowsedJob: (job: Job) => Promise<void>;
}

/**
 * Hook to manage all actions for a Hit component
 * Handles save, unsave, apply, hide, and application state changes
 */
export function useHitActions({
  hit,
  jobId,
  isSaved,
  isViewed,
  isApplied,
  isHidden,
  savedJobPayload,
  showToast,
  onMenuClose,
}: UseHitActionsProps): HitActions {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const queryClient = useQueryClient();
  const { saveJob, unsaveJob } = useSavedJobActions();
  const { recordViewedJob, setAppliedJobState, syncApplicationStateToBackend, syncJobIsHiddenToBackend, recordBrowsedJob } =
    useUserJobStateSync();
  const { isAuthenticated } = useAuth();

  const handleToggleSaved = useCallback(async () => {
    if (!isSaved) {
      trackJobSave({
        jobId: hit.objectID,
        jobTitle: hit.normalisedTitle || hit.title || '',
        locale: intl.locale,
        loggedIn: isAuthenticated,
      });
      const success = await saveJob(savedJobPayload);
      if (success) {
        const locale = normalizeSupportedLocale(intl.locale);
        const liveUserState = queryClient.getQueryData<UserStateMap>(userStateQueryKey);
        const savedCount = extractJobsFromUserState(liveUserState ?? {}).savedJobs.length;
        showToast(
          formatMessage(
            {
              id: 'hit.toast.savedWithCount',
              defaultMessage: 'Saved: {count, plural, one {# job} other {# jobs}} in My Jobs',
              description: 'Success toast shown when a job is saved, with the running count of saved jobs',
            },
            { count: savedCount }
          ),
          'success',
          3000,
          {
            label: formatMessage({ id: 'hit.toast.viewMyJobs', defaultMessage: 'View →', description: 'Toast action link into My Jobs after saving a job' }),
            onClick: () => navigate(`/${locale}${localizeRoute('/tools', locale)}`),
          }
        );
      }
    } else {
      trackJobUnsave({
        jobId: hit.objectID,
        jobTitle: hit.normalisedTitle || hit.title || '',
        locale: intl.locale,
        loggedIn: isAuthenticated,
      });
      const success = await unsaveJob(savedJobPayload);
      if (success) {
        showToast(
          formatMessage({ id: 'hit.toast.unsaved', defaultMessage: 'Job removed from saved', description: 'Info toast notification message shown when a job is removed from saved jobs' }),
          'info'
        );
      }
    }
  }, [hit, intl, isSaved, saveJob, savedJobPayload, unsaveJob, showToast, queryClient, isAuthenticated]);

  const handleMarkViewed = useCallback(async () => {
    if (!isViewed) {
      const success = await recordViewedJob(savedJobPayload);
      if (success) {
        trackEvent({
          name: 'job_mark_viewed',
          params: {
            job_id: hit.objectID,
            job_title: hit.normalisedTitle || hit.title || '',
            locale: intl.locale,
          },
        });
        showToast(
          formatMessage({ id: 'hit.toast.viewed', defaultMessage: 'Marked as viewed', description: 'Success toast notification message shown when a job is marked as viewed' }),
          'success'
        );
      }
    }
  }, [isViewed, recordViewedJob, savedJobPayload, hit, intl, showToast]);

  const handleToggleApplied = useCallback(async () => {
    const willBeApplied = !isApplied;
    const success = await setAppliedJobState(savedJobPayload, willBeApplied);
    if (success) {
      if (willBeApplied) {
        trackJobApply({
          jobId: hit.objectID,
          jobTitle: hit.normalisedTitle || hit.title || '',
          companyId: hit.companyId || '',
          source: 'tracker_marked',
          locale: intl.locale,
        });
        showToast(
          formatMessage({ id: 'hit.toast.applied', defaultMessage: 'Marked as applied', description: 'Success toast notification message shown when a job is marked as applied to' }),
          'success'
        );
      } else {
        showToast(
          formatMessage({ id: 'hit.toast.unapplied', defaultMessage: 'Removed from applied', description: 'Info toast notification message shown when applied status is removed from a job' }),
          'info'
        );
      }
    }
  }, [isApplied, setAppliedJobState, savedJobPayload, hit, intl, showToast]);

  const handleToggleHide = useCallback(() => {
    const willBeHidden = !isHidden;
    // syncJobIsHiddenToBackend's own upsert is non-fatal on failure (see its doc comment in
    // useUserJobStateSync.ts) — the toast reflects the optimistic intent, same as before.
    void syncJobIsHiddenToBackend(jobId, willBeHidden);
    showToast(
      willBeHidden
        ? formatMessage({ id: 'hit.toast.hidden', defaultMessage: 'Job hidden', description: 'Info toast notification message shown when a job is hidden' })
        : formatMessage({ id: 'hit.toast.unhidden', defaultMessage: 'Job unhidden', description: 'Success toast notification message shown when a previously hidden job is unhidden' }),
      willBeHidden ? 'info' : 'success',
      undefined,
      willBeHidden
        ? {
            // Explicit `false` rather than re-invoking handleToggleHide: this callback closes
            // over the pre-toggle `isHidden`, so calling it again here would re-derive the same
            // `willBeHidden` and hide it a second time instead of undoing the hide.
            label: formatMessage({ id: 'hit.toast.hidden.undo', defaultMessage: 'Undo', description: 'Toast action that unhides the job after accidentally hiding it' }),
            onClick: () => void syncJobIsHiddenToBackend(jobId, false),
          }
        : undefined
    );
  }, [isHidden, jobId, intl, showToast, syncJobIsHiddenToBackend, formatMessage]);

  const handleSetApplicationState = useCallback(async (state: ApplicationState) => {
    const success = await syncApplicationStateToBackend(jobId, state, savedJobPayload);

    if (success) {
      if (state !== null && !isApplied) {
        trackJobApply({
          jobId: hit.objectID,
          jobTitle: hit.normalisedTitle || hit.title || '',
          companyId: hit.companyId || '',
          source: 'tracker_marked',
          locale: intl.locale,
        });
      }

      const stateConfig = APPLICATION_STATES.find((s) => s.value === state);
      if (stateConfig) {
        showToast(
          formatMessage(
            { id: 'tools.applicationState.updated', defaultMessage: 'State updated to {state}' },
            { state: formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }) }
          ),
          'success'
        );
      }
    }
    onMenuClose?.();
  }, [jobId, intl, showToast, isApplied, syncApplicationStateToBackend, savedJobPayload, hit, onMenuClose]);

  return {
    handleToggleSaved,
    handleMarkViewed,
    handleToggleApplied,
    handleToggleHide,
    handleSetApplicationState,
    recordBrowsedJob,
  };
}
